import React, { useState } from 'react';
import { Card, Badge, Button, Form, InputGroup, Modal } from 'react-bootstrap';
import { FiTrash2, FiPlusCircle, FiCheck, FiX, FiAlertTriangle } from 'react-icons/fi';
import type { FamilyMemberResponse } from '../../../../../../Types/schema-helpers';
import { useLanguage } from '../../../../../../API/Context/LanguageContext';
import { useHealthBackgroundActions } from '../../../../../../API/Hooks/useHealthBackgroundActions';
import { FAMILY_RELATIONSHIP_MAP } from '../../../../../../ConfigFiles/maps/hereditary_relationship_map';
import { getRelationshipIcon, getLineageBadgeInfo } from './hereditaryHelpers';

interface FamilyMemberCardProps {
  member: FamilyMemberResponse;
  isProfessional: boolean;
}

export const FamilyMemberCard: React.FC<FamilyMemberCardProps> = ({ member, isProfessional }) => {
  const { lan } = useLanguage();
  const currentLanKey = (lan as number) === 0 ? 0 : 1;

  const { addRelativeCondition, deleteFamilyMember, loading } = useHealthBackgroundActions();

  const [conditionId, setConditionId] = useState('');
  const [isAddingCondition, setIsAddingCondition] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const RelationshipIcon = getRelationshipIcon(member.relationship);
  const lineage = getLineageBadgeInfo(member.relationship);

  const relationshipLabel =
    FAMILY_RELATIONSHIP_MAP.find((r) => r.id === member.relationship)?.labels[currentLanKey] || 'Familiar';

  const memberName = member.display_name ? `${member.display_name} (${relationshipLabel})` : relationshipLabel;
  const conditions = member.conditions ?? [];

  const handleAddCondition = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conditionId.trim()) return;

    const res = await addRelativeCondition(member.id, {
      condition_id: conditionId.trim(),
    });

    if (res) {
      setConditionId('');
      setIsAddingCondition(false);
    }
  };

  const handleConfirmDelete = async () => {
    await deleteFamilyMember(member.id);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <Card className="border shadow-sm h-100 bg-white">
        <Card.Header className="bg-light d-flex align-items-center justify-content-between py-2 px-3">
          <div className="d-flex align-items-center gap-2">
            <RelationshipIcon size={18} className="text-primary" />
            <span className="fw-bold">{memberName}</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg={lineage.variant} className="fw-normal">
              {currentLanKey === 0 ? lineage.labelEs : lineage.labelEn}
            </Badge>
            {isProfessional && (
              <Button
                variant="link"
                className="text-danger p-0 ms-1 border-0"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading}
                title={currentLanKey === 0 ? 'Eliminar familiar' : 'Delete member'}
              >
                <FiTrash2 size={16} />
              </Button>
            )}
          </div>
        </Card.Header>

        <Card.Body className="p-3 d-flex flex-column justify-content-between">
          <div>
            {/* Deceased Status Marker */}
            {member.is_deceased && (
              <div className="text-danger small fw-semibold mb-2">
                † {currentLanKey === 0 ? 'Fallecido' : 'Deceased'}
                {member.age_at_death ? ` (${member.age_at_death} yrs)` : ''}
                {member.cause_of_death ? `: ${member.cause_of_death}` : ''}
              </div>
            )}

            <h6 className="small fw-bold text-muted mb-2">
              {currentLanKey === 0 ? 'Antecedentes Patológicos:' : 'Pathological Conditions:'}
            </h6>

            {conditions.length === 0 ? (
              <p className="text-muted small fst-italic mb-2">
                {currentLanKey === 0 ? 'Sin afecciones registradas.' : 'No recorded conditions.'}
              </p>
            ) : (
              <div className="d-flex flex-wrap gap-1 mb-2">
                {conditions.map((c) => (
                  <Badge key={c.id} bg="danger" text="white" className="p-2 fw-normal">
                    {c.condition_detail?.name || c.condition_id}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Append Condition Form for Professionals */}
          {isProfessional && (
            <div className="mt-2 border-top pt-2">
              {!isAddingCondition ? (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="w-100 border-dashed d-flex align-items-center justify-content-center gap-1"
                  onClick={() => setIsAddingCondition(true)}
                >
                  <FiPlusCircle size={14} />
                  <span>{currentLanKey === 0 ? 'Agregar Afección' : 'Add Condition'}</span>
                </Button>
              ) : (
                <Form onSubmit={handleAddCondition}>
                  <InputGroup size="sm">
                    <Form.Control
                      placeholder={currentLanKey === 0 ? 'ID / Código CIE de Afección...' : 'Condition ID / CIE Code...'}
                      value={conditionId}
                      onChange={(e) => setConditionId(e.target.value)}
                      autoFocus
                    />
                    <Button type="submit" variant="primary" disabled={loading || !conditionId.trim()}>
                      <FiCheck size={14} />
                    </Button>
                    <Button variant="outline-secondary" onClick={() => setIsAddingCondition(false)}>
                      <FiX size={14} />
                    </Button>
                  </InputGroup>
                </Form>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* 🎯 Styled Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered size="sm">
        <Modal.Body className="text-center p-4">
          <div className="text-danger mb-3">
            <FiAlertTriangle size={42} />
          </div>
          <h6 className="fw-bold mb-2">
            {currentLanKey === 0 ? '¿Eliminar Familiar?' : 'Delete Family Member?'}
          </h6>
          <p className="text-muted small mb-4">
            {currentLanKey === 0
              ? `¿Deseas eliminar a ${memberName} y todas sus afecciones asociadas? Esta acción no se puede deshacer.`
              : `Are you sure you want to delete ${memberName} and all associated conditions? This action cannot be undone.`}
          </p>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="light"
              size="sm"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={loading}
              className="px-3"
            >
              {currentLanKey === 0 ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleConfirmDelete}
              disabled={loading}
              className="px-3"
            >
              {loading ? (currentLanKey === 0 ? 'Eliminando...' : 'Deleting...') : currentLanKey === 0 ? 'Eliminar' : 'Delete'}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};